import axios from 'axios';
import { createCorrelationId } from './correlationId';
import * as tokenStorage from '../auth/tokenStorage';
import { runSingleRefresh } from '../auth/refreshQueue';
import { AuthEventNames, logAuthEvent } from '../observability/authLogger';
import { logRequest } from '../observability/requestLogger';

const DEFAULT_TIMEOUT_MS = 30000;

let refreshHandler = null;
let logoutHandler = null;

export function configureAuthHandlers({ onRefresh, onLogout }) {
  refreshHandler = onRefresh;
  logoutHandler = onLogout;
}

function mapAxiosError(error) {
  if (error.response) {
    return {
      status: error.response.status,
      data: error.response.data,
      correlationId: error.response.headers?.['x-correlation-id'] ?? error.config?.headers?.['X-Correlation-Id'],
      message: error.response.data?.errorMessage || error.message
    };
  }
  if (error.code === 'ECONNABORTED') {
    return { status: 408, message: 'Request timeout', correlationId: error.config?.headers?.['X-Correlation-Id'] };
  }
  return { status: 0, message: error.message, correlationId: error.config?.headers?.['X-Correlation-Id'] };
}

function createClient(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const client = axios.create({
    timeout: timeoutMs,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  });

  client.interceptors.request.use((config) => {
    const correlationId = config.headers['X-Correlation-Id'] || createCorrelationId();
    config.headers['X-Correlation-Id'] = correlationId;
    config.metadata = { correlationId, startedAt: Date.now() };

    if (!config.skipAuth) {
      const accessToken = tokenStorage.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    return config;
  });

  client.interceptors.response.use(
    (response) => {
      logRequest({
        method: response.config?.method,
        url: response.config?.url,
        status: response.status,
        correlationId: response.config?.metadata?.correlationId,
        durationMs: Date.now() - (response.config?.metadata?.startedAt ?? Date.now())
      });

      const renewedAccessToken = response.headers?.['x-access-token'];
      if (renewedAccessToken && !response.config?.skipAuth) {
        tokenStorage.setTokens({ accessToken: renewedAccessToken });
      }

      return response;
    },
    async (error) => {
      const config = error.config || {};
      const mapped = mapAxiosError(error);

      if (
        error.response?.status === 401
        && !config.skipAuth
        && !config._retry
        && refreshHandler
      ) {
        config._retry = true;
        try {
          const failedAccessToken = String(config.headers?.Authorization || '')
            .replace(/^Bearer\s+/i, '')
            .trim();
          await runSingleRefresh(() => refreshHandler(failedAccessToken));
          const freshToken = tokenStorage.getAccessToken();
          if (!freshToken) {
            throw new Error('No access token after refresh');
          }
          config.headers.Authorization = `Bearer ${freshToken}`;
          logAuthEvent(AuthEventNames.REQUEST_RETRY, {
            correlationId: config.metadata?.correlationId,
            endpoint: config.url,
            durationMs: Date.now() - (config.metadata?.startedAt ?? Date.now())
          });
          return client(config);
        } catch (refreshError) {
          if (logoutHandler) {
            await logoutHandler();
          }
          return Promise.reject(mapAxiosError(refreshError));
        }
      }

      return Promise.reject(mapped);
    }
  );

  return client;
}

const http = createClient();

export async function request(config) {
  const response = await http(config);
  return {
    data: response.data,
    status: response.status,
    headers: response.headers,
    correlationId: response.config?.metadata?.correlationId
  };
}

export function get(url, config = {}) {
  return request({ ...config, method: 'GET', url });
}

export function post(url, data, config = {}) {
  return request({ ...config, method: 'POST', url, data });
}

export function put(url, data, config = {}) {
  return request({ ...config, method: 'PUT', url, data });
}

export function del(url, config = {}) {
  return request({ ...config, method: 'DELETE', url });
}

export function createCancelToken() {
  const controller = new AbortController();
  return {
    token: controller.signal,
    cancel: (reason) => controller.abort(reason)
  };
}

export { createClient, DEFAULT_TIMEOUT_MS, http };
